import { zodResolver } from '@hookform/resolvers/zod';
import { createFileRoute } from '@tanstack/react-router';
import { Check, Copy, Eye, KeyRound, MoreHorizontal, ShieldAlert, Trash2 } from 'lucide-react';
import { useEffect, useRef, useState } from 'react';
import { useForm, useWatch } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { toast } from 'sonner';

import type { DataTableColumn } from '@/components/common/data-table';
import { DataTable } from '@/components/common/data-table';
import { TablePaginationControls } from '@/components/common/table-pagination-controls';
import { useTablePagination } from '@/components/common/use-table-pagination';
import { ConfirmDialog } from '@/components/common/confirm-dialog';
import { PageWrapper } from '@/components/layout/page-wrapper';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Checkbox } from '@/components/ui/checkbox';
import {
    Dialog,
    DialogContent,
    DialogDescription,
    DialogFooter,
    DialogHeader,
    DialogTitle,
    DialogTrigger
} from '@/components/ui/dialog';
import {
    DropdownMenu,
    DropdownMenuContent,
    DropdownMenuItem,
    DropdownMenuTrigger
} from '@/components/ui/dropdown-menu';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { SubscriptionInactiveNotice } from '@/features/organizations/components/subscription-inactive-notice';
import { useCurrentOrganization } from '@/features/organizations/hooks/useCurrentOrganization';
import { useOrganizationPlatformAccess } from '@/features/organizations/hooks/useOrganizationPlatformAccess';
import { canManageOrganizationApiKeys, canManageOrganizationBilling } from '@/features/organizations/permissions';
import {
    useCreateOrganizationApiKey,
    useOrganizationApiKeyDetails,
    useOrganizationApiKeys,
    useRevokeOrganizationApiKey
} from '@/features/organizations/hooks/useOrganizationApiKeys';
import {
    createApiKeyScopeValues,
    createOrganizationApiKeyInputSchema,
    type ApiKeyScope,
    type CreateOrganizationApiKeyInput,
    type OrganizationApiKey
} from '@/features/organizations/services/api-keys';
import { ApiClientError } from '@/lib/api';
import {
    apiErrorStatusFallbackPresets,
    getTranslatedApiFieldError,
    resolveApiErrorMessage
} from '@/lib/api-error-translations';
import { requireOrganizationRoleRoute } from '@/lib/router-guards';
import { formatDate } from '@/lib/utils';

import type { ApiKeyDraft, CreateDialogStep } from './-api-keys.types';

const DEFAULT_API_KEY_SCOPES: ApiKeyScope[] = ['publishing:read'];
const API_KEYS_PAGE_SIZE = 25;
const API_KEY_SCOPE_ORDER = createApiKeyScopeValues;

const getScopeTranslationKey = (scope: string) => scope.replace(/[:.]/g, '_');

const normalizeApiKeyScopes = (scopes: string[]): string[] => {
    return Array.from(new Set(scopes)).sort((left, right) => {
        const leftIndex = API_KEY_SCOPE_ORDER.indexOf(left as ApiKeyScope);
        const rightIndex = API_KEY_SCOPE_ORDER.indexOf(right as ApiKeyScope);

        if (leftIndex === -1 && rightIndex === -1) {
            return left.localeCompare(right);
        }

        if (leftIndex === -1) {
            return 1;
        }

        if (rightIndex === -1) {
            return -1;
        }

        return leftIndex - rightIndex;
    });
};

const getApiKeyStatusBadgeClassName = (status: 'active' | 'revoked' | 'suspended') => {
    switch (status) {
        case 'active':
            return 'border-emerald-200 bg-emerald-50 text-emerald-700 dark:border-emerald-900 dark:bg-emerald-950 dark:text-emerald-300';
        case 'suspended':
            return 'border-amber-200 bg-amber-50 text-amber-700 dark:border-amber-900 dark:bg-amber-950 dark:text-amber-300';
        case 'revoked':
        default:
            return 'border-border bg-muted/60 text-muted-foreground';
    }
};

const OrganizationApiKeysPage = () => {
    const { t } = useTranslation();
    const { currentOrganization } = useCurrentOrganization();
    const platformAccess = useOrganizationPlatformAccess(
        currentOrganization?.id ?? null,
        currentOrganization?.hasActivePlatformAccess
    );
    const [isCreateDialogOpen, setIsCreateDialogOpen] = useState(false);
    const [createDialogStep, setCreateDialogStep] = useState<CreateDialogStep>('form');
    const [apiKeyDraft, setApiKeyDraft] = useState<ApiKeyDraft | null>(null);
    const [createdToken, setCreatedToken] = useState<string | null>(null);
    const [apiKeyToPreview, setApiKeyToPreview] = useState<OrganizationApiKey | null>(null);
    const [apiKeyToRevoke, setApiKeyToRevoke] = useState<OrganizationApiKey | null>(null);
    const lastListErrorMessageRef = useRef<string | null>(null);

    const pagination = useTablePagination({
        resetKey: currentOrganization?.id ?? null
    });
    const { currentPage } = pagination;
    const apiKeysQuery = useOrganizationApiKeys({
        organizationId: currentOrganization?.id ?? null,
        page: currentPage,
        pageSize: API_KEYS_PAGE_SIZE
    });
    const createApiKey = useCreateOrganizationApiKey(currentOrganization?.id ?? null);
    const revokeApiKey = useRevokeOrganizationApiKey(currentOrganization?.id ?? null);
    const apiKeyDetailsQuery = useOrganizationApiKeyDetails(
        currentOrganization?.id ?? null,
        apiKeyToPreview?.id ?? null
    );

    const canManageApiKeys = canManageOrganizationApiKeys(currentOrganization?.membership?.role);
    const canManageBilling = canManageOrganizationBilling(currentOrganization?.membership?.role);
    const apiKeys = apiKeysQuery.data?.data ?? [];
    const previewApiKeyDetails = apiKeyDetailsQuery.data;
    const previewDetailsError = apiKeyDetailsQuery.error;
    const previewDetailsEndpointUnavailable =
        previewDetailsError instanceof ApiClientError &&
        (previewDetailsError.status === 404 || previewDetailsError.status === 405);
    const previewApiKeyRecord =
        previewApiKeyDetails?.apiKey ??
        (apiKeyToPreview
            ? {
                  ...apiKeyToPreview
              }
            : null);
    const previewToken = previewApiKeyDetails?.token ?? null;
    const previewTokenAvailable = previewApiKeyDetails?.tokenAvailable ?? false;

    useEffect(() => {
        if (!apiKeysQuery.isError) {
            lastListErrorMessageRef.current = null;
            return;
        }

        const message = resolveApiKeysListError(apiKeysQuery.error, t);

        if (lastListErrorMessageRef.current === message) {
            return;
        }

        lastListErrorMessageRef.current = message;
        toast.error(message);
    }, [apiKeysQuery.error, apiKeysQuery.isError, t]);

    const {
        register,
        handleSubmit,
        reset,
        setError,
        setValue,
        control,
        formState: { errors }
    } = useForm<CreateOrganizationApiKeyInput>({
        resolver: zodResolver(createOrganizationApiKeyInputSchema),
        defaultValues: {
            name: '',
            scopes: DEFAULT_API_KEY_SCOPES
        }
    });

    const selectedScopes = useWatch({ control, name: 'scopes' });

    const toggleScope = (scope: ApiKeyScope, checked: boolean) => {
        const nextScopes = new Set(selectedScopes ?? []);

        if (checked) {
            nextScopes.add(scope);
            if (scope === 'publishing:publish') {
                nextScopes.add('publishing:read');
            }
        } else {
            nextScopes.delete(scope);
            if (scope === 'publishing:read') {
                nextScopes.delete('publishing:publish');
            }
        }

        setValue('scopes', normalizeApiKeyScopes(Array.from(nextScopes)) as ApiKeyScope[], {
            shouldDirty: true,
            shouldTouch: true,
            shouldValidate: true
        });
    };

    const getScopeLabel = (scope: string) => {
        const translationKey = `organizationApiKeys.permissions.${getScopeTranslationKey(scope)}.label`;
        const translated = t(translationKey);
        return translated === translationKey ? scope : translated;
    };

    const getScopeDescription = (scope: string) => {
        const translationKey = `organizationApiKeys.permissions.${getScopeTranslationKey(scope)}.description`;
        const translated = t(translationKey);
        return translated === translationKey ? scope : translated;
    };

    const apiKeyColumns: DataTableColumn<OrganizationApiKey>[] = [
        {
            key: 'name',
            header: t('organizationApiKeys.columns.name'),
            cell: (apiKey) => (
                <div>
                    <div className='font-medium'>{apiKey.name}</div>
                    <div className='text-sm text-muted-foreground'>{apiKey.secretPrefix}••••••••</div>
                </div>
            )
        },
        {
            key: 'status',
            header: t('organizationApiKeys.columns.status'),
            cell: (apiKey) => {
                if (apiKey.isActive && platformAccess.isInactive) {
                    return (
                        <Badge className={getApiKeyStatusBadgeClassName('suspended')}>
                            {t('organizationApiKeys.statuses.suspended')}
                        </Badge>
                    );
                }

                const status = apiKey.isActive ? 'active' : 'revoked';

                return (
                    <Badge className={getApiKeyStatusBadgeClassName(status)}>
                        {t(`organizationApiKeys.statuses.${status}`)}
                    </Badge>
                );
            }
        },
        {
            key: 'createdAt',
            header: t('organizationApiKeys.columns.createdAt'),
            cell: (apiKey) => formatDate(apiKey.createdAt)
        },
        {
            key: 'actions',
            header: t('organizationApiKeys.columns.actions'),
            className: 'w-[80px] text-right',
            cell: (apiKey) => {
                const disabled = !canManageApiKeys || !apiKey.isActive;

                return (
                    <div className='flex justify-end'>
                        <DropdownMenu>
                            <DropdownMenuTrigger asChild>
                                <Button variant='ghost' size='icon' disabled={disabled}>
                                    <MoreHorizontal className='h-4 w-4' />
                                </Button>
                            </DropdownMenuTrigger>
                            <DropdownMenuContent align='end'>
                                <DropdownMenuItem
                                    onSelect={() => {
                                        setApiKeyToPreview(apiKey);
                                    }}
                                >
                                    <Eye className='mr-2 h-4 w-4' />
                                    {t('organizationApiKeys.previewExistingAction')}
                                </DropdownMenuItem>
                                <DropdownMenuItem
                                    variant='destructive'
                                    onSelect={() => setApiKeyToRevoke(apiKey)}
                                >
                                    <Trash2 className='mr-2 h-4 w-4' />
                                    {t('organizationApiKeys.revokeAction')}
                                </DropdownMenuItem>
                            </DropdownMenuContent>
                        </DropdownMenu>
                    </div>
                );
            }
        }
    ];

    const resetCreateDialog = () => {
        setCreateDialogStep('form');
        setApiKeyDraft(null);
        setCreatedToken(null);
        reset({
            name: '',
            scopes: DEFAULT_API_KEY_SCOPES
        });
    };

    const handlePreviewApiKey = (formData: CreateOrganizationApiKeyInput) => {
        if (platformAccess.isInactive) {
            setError('root', { message: t('organizations.subscriptionInactive.actionBlockedToast') });
            return;
        }

        setApiKeyDraft({
            ...formData,
            scopes: formData.scopes
        });
        setCreateDialogStep('preview');
    };

    const handleCreateApiKey = async () => {
        if (!apiKeyDraft) {
            return;
        }

        if (platformAccess.isInactive) {
            setError('root', { message: t('organizations.subscriptionInactive.actionBlockedToast') });
            return;
        }

        try {
            const result = await createApiKey.mutateAsync(apiKeyDraft);
            setCreatedToken(result.token);
            setCreateDialogStep('result');
            toast.success(t('organizationApiKeys.apiKeyCreated'));
        } catch (error) {
            if (error instanceof ApiClientError) {
                const nameError = getTranslatedApiFieldError(error, 'name', t);

                if (nameError) {
                    setError('name', { message: nameError });
                }

                setError('root', {
                    message: resolveApiErrorMessage(error, t, {
                        fallbackKey: 'apiErrors.organizationApiKeys.actionFailed',
                        statusFallbacks: {
                            400: 'apiErrors.validation.invalidRequestBody',
                            401: 'apiErrors.auth.required',
                            403: 'apiErrors.organizationApiKeys.manageForbidden',
                            404: 'apiErrors.organizations.notFound',
                            409: 'apiErrors.organizationApiKeys.createConflict'
                        }
                    })
                });
                return;
            }

            setError('root', { message: t('apiErrors.organizationApiKeys.actionFailed') });
        }
    };

    const handleRevokeApiKey = async () => {
        if (!apiKeyToRevoke) {
            return;
        }

        try {
            await revokeApiKey.mutateAsync({ apiKeyId: apiKeyToRevoke.id });
            toast.success(t('organizationApiKeys.apiKeyRevoked'));
            setApiKeyToRevoke(null);
        } catch (error) {
            toast.error(resolveApiKeyError(error, t));
        }
    };

    const copyToken = async () => {
        if (!createdToken) {
            return;
        }

        await navigator.clipboard.writeText(createdToken);
        toast.success(t('organizationApiKeys.tokenCopied'));
    };

    const getCreateDialogTitle = () => {
        if (createDialogStep === 'preview') {
            return t('organizationApiKeys.previewTitle');
        }

        if (createDialogStep === 'result') {
            return t('organizationApiKeys.resultTitle');
        }

        return t('organizationApiKeys.dialogTitle');
    };

    const getCreateDialogDescription = () => {
        if (createDialogStep === 'preview') {
            return t('organizationApiKeys.previewDescription');
        }

        if (createDialogStep === 'result') {
            return t('organizationApiKeys.resultDescription');
        }

        return t('organizationApiKeys.dialogDescription');
    };

    return (
        <>
            <PageWrapper
                title={t('organizationApiKeys.pageTitle')}
                description={t('organizationApiKeys.pageDescription', {
                    organizationName: currentOrganization?.name ?? t('organizationApiKeys.fallbackOrganizationName')
                })}
                actions={
                    <Dialog
                        open={isCreateDialogOpen}
                        onOpenChange={(open) => {
                            setIsCreateDialogOpen(open);
                            if (!open) {
                                resetCreateDialog();
                            }
                        }}
                    >
                        <DialogTrigger asChild>
                            <Button
                                disabled={!canManageApiKeys || platformAccess.isPending || platformAccess.isInactive}
                                title={
                                    platformAccess.isInactive
                                        ? t('organizations.subscriptionInactive.actionBlockedToast')
                                        : undefined
                                }
                            >
                                <KeyRound className='h-4 w-4' />
                                {t('organizationApiKeys.createAction')}
                            </Button>
                        </DialogTrigger>
                        <DialogContent>
                            <DialogHeader>
                                <DialogTitle>{getCreateDialogTitle()}</DialogTitle>
                                <DialogDescription>{getCreateDialogDescription()}</DialogDescription>
                            </DialogHeader>

                            {createDialogStep === 'form' ? (
                                <form onSubmit={handleSubmit(handlePreviewApiKey)} className='space-y-4'>
                                    <div className='space-y-2'>
                                        <Label htmlFor='api-key-name'>{t('organizationApiKeys.fields.name')}</Label>
                                        <Input id='api-key-name' {...register('name')} />
                                        {errors.name ? (
                                            <p className='text-sm text-destructive'>{errors.name.message}</p>
                                        ) : null}
                                    </div>

                                    <div className='space-y-3'>
                                        <div className='space-y-1'>
                                            <Label>{t('organizationApiKeys.fields.permissions')}</Label>
                                            <p className='text-sm text-muted-foreground'>
                                                {t('organizationApiKeys.permissionsDescription')}
                                            </p>
                                        </div>
                                        <div className='space-y-2'>
                                            <div className='flex items-start gap-3 rounded-lg border p-3'>
                                                <Checkbox
                                                    id='api-key-scope-publishing-read'
                                                    checked={selectedScopes?.includes('publishing:read') ?? false}
                                                    onCheckedChange={(checked) =>
                                                        toggleScope('publishing:read', checked === true)
                                                    }
                                                    disabled={selectedScopes?.includes('publishing:publish') ?? false}
                                                />
                                                <div className='space-y-1'>
                                                    <Label
                                                        htmlFor='api-key-scope-publishing-read'
                                                        className='cursor-pointer'
                                                    >
                                                        {t('organizationApiKeys.permissions.publishing_read.label')}
                                                    </Label>
                                                    <p className='text-sm text-muted-foreground'>
                                                        {t(
                                                            'organizationApiKeys.permissions.publishing_read.description'
                                                        )}
                                                    </p>
                                                </div>
                                            </div>
                                            <div className='flex items-start gap-3 rounded-lg border p-3'>
                                                <Checkbox
                                                    id='api-key-scope-publishing-publish'
                                                    checked={selectedScopes?.includes('publishing:publish') ?? false}
                                                    onCheckedChange={(checked) =>
                                                        toggleScope('publishing:publish', checked === true)
                                                    }
                                                />
                                                <div className='space-y-1'>
                                                    <Label
                                                        htmlFor='api-key-scope-publishing-publish'
                                                        className='cursor-pointer'
                                                    >
                                                        {t('organizationApiKeys.permissions.publishing_publish.label')}
                                                    </Label>
                                                    <p className='text-sm text-muted-foreground'>
                                                        {t(
                                                            'organizationApiKeys.permissions.publishing_publish.description'
                                                        )}
                                                    </p>
                                                </div>
                                            </div>
                                            <div className='flex items-start gap-3 rounded-lg border p-3'>
                                                <Checkbox
                                                    id='api-key-scope-insights-read'
                                                    checked={selectedScopes?.includes('insights:read') ?? false}
                                                    onCheckedChange={(checked) =>
                                                        toggleScope('insights:read', checked === true)
                                                    }
                                                />
                                                <div className='space-y-1'>
                                                    <Label
                                                        htmlFor='api-key-scope-insights-read'
                                                        className='cursor-pointer'
                                                    >
                                                        {t('organizationApiKeys.permissions.insights_read.label')}
                                                    </Label>
                                                    <p className='text-sm text-muted-foreground'>
                                                        {t('organizationApiKeys.permissions.insights_read.description')}
                                                    </p>
                                                </div>
                                            </div>
                                        </div>
                                        <p className='text-sm text-muted-foreground'>
                                            {t('organizationApiKeys.permissionsPublishIncludesRead')}
                                        </p>
                                        <p className='text-sm text-muted-foreground'>
                                            {t('organizationApiKeys.permissionsInsightsSeparate')}
                                        </p>
                                        {errors.scopes ? (
                                            <p className='text-sm text-destructive'>{errors.scopes.message}</p>
                                        ) : null}
                                    </div>

                                    {errors.root?.message ? (
                                        <p className='text-sm text-destructive'>{errors.root.message}</p>
                                    ) : null}

                                    <DialogFooter>
                                        <Button
                                            type='button'
                                            variant='outline'
                                            onClick={() => setIsCreateDialogOpen(false)}
                                        >
                                            {t('common.cancel')}
                                        </Button>
                                        <Button type='submit'>{t('organizationApiKeys.previewAction')}</Button>
                                    </DialogFooter>
                                </form>
                            ) : null}

                            {createDialogStep === 'preview' && apiKeyDraft ? (
                                <div className='space-y-4'>
                                    <div className='space-y-2 rounded-lg border bg-muted/30 p-4'>
                                        <div>
                                            <p className='text-sm text-muted-foreground'>
                                                {t('organizationApiKeys.fields.name')}
                                            </p>
                                            <p className='font-medium'>{apiKeyDraft.name}</p>
                                        </div>
                                    </div>

                                    <div className='space-y-3 rounded-lg border bg-muted/30 p-4'>
                                        <div>
                                            <p className='font-medium'>{t('organizationApiKeys.fields.permissions')}</p>
                                            <p className='text-sm text-muted-foreground'>
                                                {t('organizationApiKeys.previewPermissionsDescription')}
                                            </p>
                                        </div>
                                        <div className='space-y-2'>
                                            {apiKeyDraft.scopes.map((scope) => (
                                                <div key={scope} className='flex items-center gap-2 text-sm'>
                                                    <Check className='h-4 w-4 text-primary' />
                                                    <span>{getScopeLabel(scope)}</span>
                                                </div>
                                            ))}
                                        </div>
                                    </div>

                                    {errors.root?.message ? (
                                        <p className='text-sm text-destructive'>{errors.root.message}</p>
                                    ) : null}

                                    <DialogFooter>
                                        <Button
                                            type='button'
                                            variant='outline'
                                            onClick={() => setCreateDialogStep('form')}
                                        >
                                            {t('organizationApiKeys.backToEdit')}
                                        </Button>
                                        <Button
                                            type='button'
                                            onClick={() => void handleCreateApiKey()}
                                            disabled={createApiKey.isPending}
                                        >
                                            {createApiKey.isPending
                                                ? t('organizationApiKeys.creating')
                                                : t('organizationApiKeys.createSubmit')}
                                        </Button>
                                    </DialogFooter>
                                </div>
                            ) : null}

                            {createDialogStep === 'result' && apiKeyDraft && createdToken ? (
                                <div className='space-y-4'>
                                    <div className='space-y-3 rounded-lg border bg-muted/30 p-4'>
                                        <div>
                                            <p className='font-medium'>{t('organizationApiKeys.tokenTitle')}</p>
                                            <p className='text-sm text-muted-foreground'>
                                                {t('organizationApiKeys.tokenDescription')}
                                            </p>
                                        </div>
                                        <div className='flex gap-2'>
                                            <Input value={createdToken} readOnly />
                                            <Button type='button' variant='outline' onClick={() => void copyToken()}>
                                                <Copy className='h-4 w-4' />
                                                {t('organizationApiKeys.copyToken')}
                                            </Button>
                                        </div>
                                    </div>

                                    <div className='space-y-3 rounded-lg border bg-muted/30 p-4'>
                                        <div>
                                            <p className='font-medium'>
                                                {t('organizationApiKeys.previewSummaryTitle')}
                                            </p>
                                            <p className='text-sm text-muted-foreground'>
                                                {t('organizationApiKeys.previewSummaryDescription')}
                                            </p>
                                        </div>
                                        <div className='space-y-2'>
                                            <div>
                                                <p className='text-sm text-muted-foreground'>
                                                    {t('organizationApiKeys.fields.name')}
                                                </p>
                                                <p className='font-medium'>{apiKeyDraft.name}</p>
                                            </div>
                                            <div>
                                                <p className='text-sm text-muted-foreground'>
                                                    {t('organizationApiKeys.fields.permissions')}
                                                </p>
                                                <div className='mt-2 space-y-2'>
                                                    {apiKeyDraft.scopes.map((scope) => (
                                                        <div key={scope} className='flex items-center gap-2 text-sm'>
                                                            <Check className='h-4 w-4 text-primary' />
                                                            <span>{getScopeLabel(scope)}</span>
                                                        </div>
                                                    ))}
                                                </div>
                                            </div>
                                        </div>
                                    </div>

                                    <DialogFooter>
                                        <Button type='button' onClick={() => setIsCreateDialogOpen(false)}>
                                            {t('common.close')}
                                        </Button>
                                    </DialogFooter>
                                </div>
                            ) : null}
                        </DialogContent>
                    </Dialog>
                }
            >
                {!canManageApiKeys ? (
                    <div className='flex items-start gap-3 rounded-lg border border-dashed bg-muted/30 p-4 text-sm text-muted-foreground'>
                        <ShieldAlert className='mt-0.5 h-4 w-4 shrink-0' />
                        <p>{t('organizationApiKeys.permissionHint')}</p>
                    </div>
                ) : null}

                {platformAccess.isInactive ? (
                    <SubscriptionInactiveNotice
                        title={t('organizations.subscriptionInactive.title')}
                        description={t('organizationApiKeys.subscriptionInactiveDescription')}
                        actionLabel={t('organizations.subscriptionInactive.manageBillingAction')}
                        ownerHint={t('organizations.subscriptionInactive.ownerHint')}
                        canManageBilling={canManageBilling}
                    />
                ) : null}

                <div className='space-y-4'>
                    <DataTable
                        columns={apiKeyColumns}
                        data={apiKeys}
                        isLoading={apiKeysQuery.isPending}
                        rowKey={(apiKey) => apiKey.id}
                        emptyMessage={t('organizationApiKeys.empty')}
                    />
                    <TablePaginationControls
                        page={pagination.currentPage}
                        totalPages={apiKeysQuery.data?.totalPages ?? 1}
                        count={apiKeys.length}
                        total={apiKeysQuery.data?.total ?? apiKeys.length}
                        isPending={apiKeysQuery.isPending}
                        onPrevious={pagination.onPrevious}
                        onNext={pagination.onNext}
                    />
                </div>
            </PageWrapper>

            <Dialog
                open={Boolean(apiKeyToPreview)}
                onOpenChange={(open) => {
                    if (!open) {
                        setApiKeyToPreview(null);
                    }
                }}
            >
                <DialogContent variant='scroll' className='sm:max-w-lg'>
                    <div className='shrink-0 px-6 pt-6'>
                        <DialogHeader>
                            <DialogTitle>{t('organizationApiKeys.previewExistingTitle')}</DialogTitle>
                            <DialogDescription>{t('organizationApiKeys.previewExistingDescription')}</DialogDescription>
                        </DialogHeader>
                    </div>

                    <div className='min-h-0 flex-1 overflow-y-auto px-6 py-4'>
                        {apiKeyToPreview ? (
                            apiKeyDetailsQuery.isPending ? (
                                <div className='rounded-lg border bg-muted/30 p-4 text-sm text-muted-foreground'>
                                    {t('organizationApiKeys.previewExistingLoading')}
                                </div>
                            ) : apiKeyDetailsQuery.isError && !previewDetailsEndpointUnavailable ? (
                                <div className='rounded-lg border border-destructive/40 bg-destructive/5 p-4 text-sm text-destructive'>
                                    {resolveApiKeyDetailsError(apiKeyDetailsQuery.error, t)}
                                </div>
                            ) : previewApiKeyRecord ? (
                                <div className='space-y-4'>
                                    {previewDetailsEndpointUnavailable ? (
                                        <div className='rounded-lg border bg-muted/30 p-4 text-sm text-muted-foreground'>
                                            {t('organizationApiKeys.previewExistingEndpointUnavailable')}
                                        </div>
                                    ) : null}

                                    <div className='space-y-3 rounded-lg border bg-muted/30 p-4'>
                                        <div>
                                            <p className='text-sm text-muted-foreground'>
                                                {t('organizationApiKeys.fields.name')}
                                            </p>
                                            <p className='font-medium'>{previewApiKeyRecord.name}</p>
                                        </div>
                                        <div>
                                            <p className='text-sm text-muted-foreground'>
                                                {previewToken
                                                    ? t('organizationApiKeys.previewFields.fullKey')
                                                    : t('organizationApiKeys.previewFields.maskedKey')}
                                            </p>
                                            <p className='font-mono text-sm break-all'>
                                                {previewToken ?? `${previewApiKeyRecord.secretPrefix}••••••••`}
                                            </p>
                                        </div>
                                        <div>
                                            <p className='text-sm text-muted-foreground'>
                                                {t('organizationApiKeys.columns.status')}
                                            </p>
                                            <div className='mt-1'>
                                                <Badge
                                                    className={getApiKeyStatusBadgeClassName(
                                                        previewApiKeyRecord.isActive ? 'active' : 'revoked'
                                                    )}
                                                >
                                                    {previewApiKeyRecord.isActive
                                                        ? t('organizationApiKeys.statuses.active')
                                                        : t('organizationApiKeys.statuses.revoked')}
                                                </Badge>
                                            </div>
                                        </div>
                                        <div>
                                            <p className='text-sm text-muted-foreground'>
                                                {t('organizationApiKeys.columns.createdAt')}
                                            </p>
                                            <p className='font-medium'>{formatDate(previewApiKeyRecord.createdAt)}</p>
                                        </div>
                                        {previewApiKeyRecord.revokedAt ? (
                                            <div>
                                                <p className='text-sm text-muted-foreground'>
                                                    {t('organizationApiKeys.previewFields.revokedAt')}
                                                </p>
                                                <p className='font-medium'>
                                                    {formatDate(previewApiKeyRecord.revokedAt)}
                                                </p>
                                            </div>
                                        ) : null}
                                    </div>

                                    <div className='space-y-3 rounded-lg border bg-muted/30 p-4'>
                                        <div>
                                            <p className='font-medium'>{t('organizationApiKeys.fields.permissions')}</p>
                                            <p className='text-sm text-muted-foreground'>
                                                {t('organizationApiKeys.previewExistingPermissionsDescription')}
                                            </p>
                                        </div>

                                        {previewApiKeyRecord.scopes.length > 0 ? (
                                            <div className='space-y-2'>
                                                {normalizeApiKeyScopes(previewApiKeyRecord.scopes).map((scope) => (
                                                    <div key={scope} className='flex items-center gap-2 text-sm'>
                                                        <Check className='h-4 w-4 text-primary' />
                                                        <div>
                                                            <p>{getScopeLabel(scope)}</p>
                                                            <p className='text-muted-foreground'>
                                                                {getScopeDescription(scope)}
                                                            </p>
                                                        </div>
                                                    </div>
                                                ))}
                                            </div>
                                        ) : (
                                            <div className='rounded-lg border border-dashed p-4 text-sm text-muted-foreground'>
                                                {t('organizationApiKeys.previewExistingNoScopes')}
                                            </div>
                                        )}
                                    </div>

                                    <div className='space-y-3 rounded-lg border bg-muted/30 p-4'>
                                        <div>
                                            <p className='font-medium'>{t('organizationApiKeys.tokenTitle')}</p>
                                            <p className='text-sm text-muted-foreground'>
                                                {previewTokenAvailable
                                                    ? t('organizationApiKeys.previewExistingTokenAvailable')
                                                    : t('organizationApiKeys.previewExistingTokenUnavailable')}
                                            </p>
                                        </div>

                                        {previewToken ? (
                                            <div className='space-y-3'>
                                                <Input value={previewToken} readOnly appearance='code' />
                                                <Button
                                                    type='button'
                                                    variant='outline'
                                                    onClick={() => {
                                                        void navigator.clipboard.writeText(previewToken);
                                                        toast.success(t('organizationApiKeys.tokenCopied'));
                                                    }}
                                                >
                                                    <Copy className='mr-2 h-4 w-4' />
                                                    {t('organizationApiKeys.copyToken')}
                                                </Button>
                                            </div>
                                        ) : (
                                            <div className='rounded-lg border border-dashed p-4 text-sm text-muted-foreground'>
                                                {t('organizationApiKeys.previewExistingLimitation')}
                                            </div>
                                        )}
                                    </div>
                                </div>
                            ) : (
                                <div className='rounded-lg border bg-muted/30 p-4 text-sm text-muted-foreground'>
                                    {t('organizationApiKeys.previewExistingEmpty')}
                                </div>
                            )
                        ) : null}
                    </div>

                    <div className='shrink-0 px-6 pb-6'>
                        <DialogFooter>
                            <Button type='button' onClick={() => setApiKeyToPreview(null)}>
                                {t('common.close')}
                            </Button>
                        </DialogFooter>
                    </div>
                </DialogContent>
            </Dialog>

            <ConfirmDialog
                open={Boolean(apiKeyToRevoke)}
                onOpenChange={(open) => {
                    if (!open) {
                        setApiKeyToRevoke(null);
                    }
                }}
                title={t('organizationApiKeys.confirmRevokeTitle')}
                description={t('organizationApiKeys.confirmRevokeDescription', {
                    apiKeyName: apiKeyToRevoke?.name ?? ''
                })}
                confirmLabel={t('organizationApiKeys.revokeAction')}
                variant='destructive'
                onConfirm={() => void handleRevokeApiKey()}
                isLoading={revokeApiKey.isPending}
            />
        </>
    );
};

export const Route = createFileRoute('/_app/api-keys')({
    beforeLoad: () => requireOrganizationRoleRoute(canManageOrganizationApiKeys),
    component: OrganizationApiKeysPage
});

const resolveApiKeysListError = (
    error: unknown,
    t: (key: string, options?: Record<string, unknown>) => string
): string => {
    return resolveApiErrorMessage(error, t, {
        fallbackKey: 'apiErrors.organizationApiKeys.actionFailed',
        statusFallbacks: apiErrorStatusFallbackPresets.organizationApiKeysList
    });
};

const resolveApiKeyError = (error: unknown, t: (key: string, options?: Record<string, unknown>) => string): string => {
    return resolveApiErrorMessage(error, t, {
        fallbackKey: 'apiErrors.organizationApiKeys.actionFailed',
        statusFallbacks: apiErrorStatusFallbackPresets.organizationApiKeysRevoke
    });
};

const resolveApiKeyDetailsError = (
    error: unknown,
    t: (key: string, options?: Record<string, unknown>) => string
): string => {
    return resolveApiErrorMessage(error, t, {
        fallbackKey: 'apiErrors.organizationApiKeys.actionFailed',
        statusFallbacks: apiErrorStatusFallbackPresets.organizationApiKeysDetails
    });
};
